// Commission payee DAO — CRUD + list for commission_payees.

import { BaseDao, type SupabaseClient, type MutationOptions } from '../base.ts';
import type { CommissionPayee } from '@shared/types/commission.ts';

const PAYEE_SELECT = '*, employees(id, full_name), companies(id, short_name)';

export class CommissionPayeeDao extends BaseDao<CommissionPayee> {
  protected readonly dependentResources = ['commission_payees'];
  protected readonly defaultSelect = PAYEE_SELECT;

  constructor(sb: SupabaseClient) {
    super(sb, 'commission_payees');
  }

  async list(opts?: { includeDeleted?: boolean }): Promise<CommissionPayee[]> {
    let q = this.sb.from('commission_payees').select(PAYEE_SELECT).order('name');
    if (!opts?.includeDeleted) q = q.eq('is_deleted', false);
    const { data, error } = await q;
    if (error) throw error;
    return (data ?? []) as CommissionPayee[];
  }

  async listByEmployee(employeeId: number): Promise<CommissionPayee[]> {
    const { data, error } = await this.sb
      .from('commission_payees')
      .select(PAYEE_SELECT)
      .eq('employee_id', employeeId)
      .eq('is_deleted', false)
      .order('name');
    if (error) throw error;
    return (data ?? []) as CommissionPayee[];
  }

  async softDelete(id: number, opts?: MutationOptions): Promise<void> {
    await this.update(id, { is_deleted: true } as Partial<CommissionPayee>, opts);
  }

  /** Find the employee's own (non-B2B) payee id, if any. */
  async findOwnIdByEmployee(employeeId: number): Promise<number | null> {
    const { data, error } = await this.sb
      .from('commission_payees')
      .select('id')
      .eq('employee_id', employeeId)
      .is('company_id', null)
      .eq('is_deleted', false)
      .limit(1);
    if (error) throw error;
    return (data?.[0] as { id: number } | undefined)?.id ?? null;
  }
}
